import { useEffect, useState } from "react";
import Header from "../components/Header";
import api from "../lib/api";
import { toast, NoticeRegion } from "../lib/notices";
import { Users, Plus, UserPlus, Trash2, X, Mail, ShieldCheck, Eye, Loader2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const ROLE_META = {
  owner: { label: "OWNER", icon: ShieldCheck, tint: "bg-brand text-ink-on-accent" },
  editor: { label: "EDITOR", icon: UserPlus, tint: "bg-surface-inverse text-white" },
  viewer: { label: "VIEWER", icon: Eye, tint: "bg-surface-sunken text-ink" },
};

function RoleBadge({ role }) {
  const meta = ROLE_META[role] || ROLE_META.viewer;
  const I = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-2xs font-mono-ui uppercase tracking-widest ${meta.tint}`} data-testid={`role-${role}`}>
      <I size={10} /> {meta.label}
    </span>
  );
}

function InviteModal({ team, onClose, onDone }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("editor");
  const [busy, setBusy] = useState(false);

  /* Escape-to-close plus focus restore, mirroring the disclosure
     pattern in components/AccountMenu.jsx. Without the restore,
     dismissing the dialog strands focus on <body> and a keyboard
     user has to Tab from the top of the document again.

     The opener is captured in a lazy useState initializer, not in
     the effect: the Email field below is autoFocus, and React
     applies that during commit — i.e. before effects run — so by
     effect time document.activeElement is already the input. The
     initializer runs during render, while the trigger still has
     focus. */
  const [opener] = useState(() =>
    typeof document === "undefined" ? null : document.activeElement
  );
  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      // Skip if the opener was itself unmounted along the way.
      if (opener?.isConnected) opener.focus?.();
    };
  }, [onClose, opener]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Email required"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/teams/${team.team_id}/invite`, { email: email.trim(), role });
      toast.success(r.data.status === "added" ? "Member added" : "Invite queued (pending signup)");
      onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-modal bg-black/40 flex items-center justify-center p-4" data-testid="invite-modal">
      <div
        className="bg-white brutal-border max-w-md w-full shadow-brutal-lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-modal-title"
      >
        <div className="flex justify-between items-center px-5 py-3 brutal-border border-l-0 border-r-0 border-t-0">
          <h2 id="invite-modal-title" className="marquee-tag">// INVITE MEMBER</h2>
          <button onClick={onClose} className="brutal-btn brutal-btn-ghost !p-1" data-testid="invite-close" aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <div className="font-display font-black type-subheading tracking-tighter mb-1">{team.name}</div>
            <p className="text-xs uppercase tracking-widest text-ink-muted">Add a teammate by email. New signups become active on invite acceptance.</p>
          </div>
          <div>
            <label className="brutal-label" htmlFor="email">Email</label>
            <div className="relative">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
              <input id="email"
                data-testid="invite-email"
                type="email"
                required
                autoFocus
                className="brutal-input pl-9"
                placeholder="teammate@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="brutal-label" htmlFor="invite-role">Role</label>
            <select id="invite-role" data-testid="invite-role" className="brutal-select" value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="editor">Editor — can create & edit projects</option>
              <option value="viewer">Viewer — read-only access</option>
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="brutal-btn brutal-btn-secondary" data-testid="invite-cancel">Cancel</button>
            <button type="submit" disabled={busy} className="brutal-btn" data-testid="invite-submit">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Send Invite
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Teams() {
  const [teams, setTeams] = useState(null);
  const [selected, setSelected] = useState(null);
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [showInvite, setShowInvite] = useState(false);

  const loadTeams = async () => {
    try {
      const r = await api.get("/teams");
      setTeams(r.data);
      if (r.data.length && !selected) setSelected(r.data[0]);
    } catch { toast.error("Failed to load teams"); }
  };

  const loadMembers = async (team) => {
    if (!team) { setMembers([]); return; }
    setLoadingMembers(true);
    try {
      const r = await api.get(`/teams/${team.team_id}/members`);
      setMembers(r.data);
    } catch { setMembers([]); toast.error("Failed to load members"); }
    finally { setLoadingMembers(false); }
  };

  useEffect(() => { loadTeams(); }, []);
  useEffect(() => { if (selected) loadMembers(selected); }, [selected?.team_id]);

  const createTeam = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const r = await api.post("/teams", { name: newName.trim() });
      setNewName("");
      await loadTeams();
      setSelected(r.data);
      toast.success(`Team "${r.data.name}" created`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setCreating(false); }
  };

  const removeMember = async (member) => {
    if (!selected || !member.user_id) return;
    if (!window.confirm(`Remove ${member.email || member.name || member.user_id}?`)) return;
    try {
      await api.delete(`/teams/${selected.team_id}/members/${member.user_id}`);
      toast.success("Member removed");
      loadMembers(selected);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const isOwner = selected?.role === "owner";

  return (
    <div className="min-h-screen">
      <Header />
      <main id="main" tabIndex={-1} className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        <NoticeRegion />
        <div className="flex flex-wrap justify-between items-end gap-4 mb-10">
          <div>
            <div className="marquee-tag mb-2">// COLLABORATION</div>
            <h1 className="font-display font-black type-title tracking-tighter">TEAMS.</h1>
            <p className="text-ink-muted mt-2 max-w-xl">Create a workspace and invite teammates to co-edit projects. Owners administer the team, editors create & edit, viewers can only read.</p>
          </div>
        </div>

        <form onSubmit={createTeam} className="brutal-card mb-8 flex flex-wrap items-end gap-3" data-testid="team-create-form">
          <div className="flex-1 min-w-[220px]">
            <label className="brutal-label" htmlFor="create-new-team">Create new team</label>
            <input id="create-new-team"
              data-testid="team-name-input"
              className="brutal-input"
              placeholder="e.g. Acme Marketing"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
          </div>
          <button type="submit" disabled={creating || !newName.trim()} className="brutal-btn" data-testid="team-create-btn">
            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create Team
          </button>
        </form>

        {teams === null && (
          <div className="text-center text-ink-muted py-16"><Loader2 className="animate-spin inline mr-2" /> Loading teams…</div>
        )}

        {teams && teams.length === 0 && (
          <div className="brutal-card p-16 text-center" data-testid="teams-empty">
            <div className="marquee-tag mb-4">// NO TEAMS YET</div>
            <Users size={48} className="mx-auto mb-4 text-ink-muted" strokeWidth={1.5} />
            <h2 className="font-display font-black type-heading mb-2 tracking-tighter">START A WORKSPACE.</h2>
            <p className="text-ink-muted">Create your first team above to invite teammates and share projects.</p>
          </div>
        )}

        {teams && teams.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6" data-testid="teams-layout">
            {/* Team list */}
            <div className="space-y-2" data-testid="teams-list">
              <h2 className="marquee-tag mb-2">// MY TEAMS ({teams.length})</h2>
              {teams.map((t) => (
                <button
                  key={t.team_id}
                  type="button"
                  onClick={() => setSelected(t)}
                  aria-pressed={selected?.team_id === t.team_id}
                  data-testid={`team-item-${t.team_id}`}
                  className={`w-full text-left brutal-border p-3 transition-colors ${selected?.team_id === t.team_id ? "bg-surface-inverse text-white on-ink" : "bg-white hover:bg-surface"}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display font-bold type-subheading leading-tight break-words">{t.name}</h3>
                    <RoleBadge role={t.role} />
                  </div>
                  <div className={`text-2xs uppercase tracking-widest mt-1 ${selected?.team_id === t.team_id ? "text-surface/70" : "text-ink-subtle"}`}>
                    {t.created_at ? formatDistanceToNow(new Date(t.created_at), { addSuffix: true }) : ""}
                  </div>
                </button>
              ))}
            </div>

            {/* Team detail */}
            <div className="brutal-card" data-testid="team-detail">
              {!selected && (
                <div className="text-ink-muted text-center py-16">Select a team to see members.</div>
              )}
              {selected && (
                <>
                  <div className="flex flex-wrap justify-between items-start gap-3 mb-6">
                    <div>
                      <div className="marquee-tag mb-2">// TEAM · <RoleBadge role={selected.role} /></div>
                      <h2 className="font-display font-black type-heading tracking-tighter" data-testid="team-detail-name">{selected.name}</h2>
                    </div>
                    {isOwner && (
                      <button onClick={() => setShowInvite(true)} className="brutal-btn" data-testid="btn-open-invite">
                        <UserPlus size={14} /> Invite member
                      </button>
                    )}
                  </div>

                  <h3 className="marquee-tag mb-3">// MEMBERS ({members.length})</h3>
                  {loadingMembers ? (
                    <div className="py-8 text-center text-ink-muted text-sm"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading members…</div>
                  ) : (
                    <div className="divide-y brutal-border border-l-0 border-r-0" data-testid="members-list">
                      {members.length === 0 && <div className="py-4 text-sm text-ink-muted">No members yet.</div>}
                      {members.map((m, i) => (
                        <div key={m.user_id || m.email || `m-${i}`} className="flex items-center justify-between py-3 gap-3" data-testid={`member-row-${i}`}>
                          <div className="flex items-center gap-3 min-w-0">
                            {m.picture ? (
                              <img src={m.picture} alt="" className="w-9 h-9 brutal-border object-cover" />
                            ) : (
                              <div className="w-9 h-9 brutal-border bg-surface flex items-center justify-center font-display font-bold text-sm">
                                {(m.email || m.name || "?").slice(0, 1).toUpperCase()}
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="font-mono-ui text-sm truncate">{m.email || m.name || m.user_id}</div>
                              <div className="text-2xs uppercase tracking-widest text-ink-subtle">
                                {m.status === "pending" ? "PENDING INVITE" : m.name || ""}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <RoleBadge role={m.role} />
                            {isOwner && m.role !== "owner" && m.user_id && (
                              <button
                                onClick={() => removeMember(m)}
                                className="brutal-btn brutal-btn-ghost !p-2"
                                aria-label="Remove"
                                data-testid={`btn-remove-${i}`}
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </main>

      {showInvite && selected && (
        <InviteModal
          team={selected}
          onClose={() => setShowInvite(false)}
          onDone={() => loadMembers(selected)}
        />
      )}
    </div>
  );
}
